import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { toAuthUrl } from "@/components/PhotoUpload";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Trophy, Users, CalendarDays, MapPin, Clock, Activity, LogOut,
  User, ShieldCheck, Star, Heart, Megaphone, AlertTriangle, Info, Pin,
} from "lucide-react";
import HelpButton from "@/components/HelpButton";

const EVENT_LABELS = { match: "Match", entrainement: "Entraînement", evenement: "Événement" };

const PRIORITY = {
  info: { label: "Info", icon: Info, color: "border-blue-300 bg-blue-50 text-blue-800" },
  important: { label: "Important", icon: AlertTriangle, color: "border-amber-300 bg-amber-50 text-amber-800" },
  urgent: { label: "Urgent", icon: AlertTriangle, color: "border-red-300 bg-red-50 text-red-800" },
};

export default function Portail() {
  const { user, logout } = useAuth();
  const [player, setPlayer] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [matchStats, setMatchStats] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tab, setTab] = useState("profil");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/me/player").then((r) => setPlayer(r.data)).catch((e) => {
      setError(e.response?.data?.detail || "Profil indisponible");
    });
    api.get("/me/calendar").then((r) => setCalendar(r.data)).catch(() => {});
    api.get("/me/match-stats").then((r) => setMatchStats(r.data)).catch(() => {});
    api.get("/me/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);

  const markRead = async (aid) => {
    await api.post(`/me/announcements/${aid}/read`).catch(() => {});
    setAnnouncements((arr) => arr.map((a) => a.id === aid ? { ...a, read: true } : a));
  };

  const unreadCount = announcements.filter((a) => !a.read).length;

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <img src={LOGO_URL} alt="AMAL VBC" className="h-16 mb-6" />
        <h1 className="font-barlow font-bold text-2xl mb-2">Profil non disponible</h1>
        <p className="text-sm text-slate-600 max-w-md">{error}</p>
        <p className="text-sm text-slate-500 max-w-md mt-3">
          Demandez à un administrateur de lier votre compte à votre fiche joueuse.
        </p>
        <Button onClick={logout} variant="outline" className="mt-6" data-testid="portail-logout">
          <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
        </Button>
      </div>
    );
  }

  if (!player) return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement...</div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = calendar.filter((e) => e.date >= today).slice(0, 5);
  const past = matchStats.slice(0, 10);

  const totalMatches = matchStats.length;
  const totalPts = matchStats.reduce((s, m) => s + (m.points || 0), 0);
  const totalAst = matchStats.reduce((s, m) => s + (m.assists || 0), 0);
  const totalReb = matchStats.reduce((s, m) => s + (m.rebounds || 0), 0);
  const avgPts = totalMatches ? (totalPts / totalMatches).toFixed(1) : "—";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portail-page">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AMAL VBC" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-barlow font-bold text-lg leading-none">AMAL VBC</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-600">Portail Joueuse</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold">{user.full_name}</div>
              <div className="text-xs text-slate-500">{user.role === "parent" ? "Parent" : "Joueuse"}</div>
            </div>
            <Button onClick={logout} variant="outline" size="sm" data-testid="portail-logout">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Quitter
            </Button>
          </div>
        </div>
      </header>

      {/* Hero card */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-6 text-white flex items-center gap-6 flex-wrap">
            <div className="h-24 w-24 rounded-md border-2 border-white/50 overflow-hidden bg-white/10 flex items-center justify-center shrink-0">
              {player.photo_url ? (
                <img src={toAuthUrl(player.photo_url)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="font-barlow font-bold text-4xl">
                  {player.first_name?.[0]}{player.last_name?.[0]}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-100">
                #{player.jersey_number ?? "—"} • {player.category} • {player.position || "—"}
              </div>
              <div className="font-barlow font-bold text-3xl sm:text-4xl tracking-tight leading-none mt-1">
                {player.first_name} {player.last_name}
              </div>
              <div className="text-sm text-emerald-100 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <span>N° licence : <b className="text-white">{player.license_number || "—"}</b></span>
                <span>•</span>
                <span>{player.nationality}</span>
                {player.height_cm && <><span>•</span><span>{player.height_cm} cm</span></>}
              </div>
            </div>
            <Badge className={`text-xs px-3 py-1.5 ${player.status === "active"
              ? "bg-white text-emerald-700"
              : player.status === "injured"
              ? "bg-red-500/30 text-red-100 border-red-300"
              : "bg-white/20 text-white border-white/30"}`}>
              {player.status === "active" ? "Active" : player.status === "injured" ? "Blessée" : "Inactive"}
            </Badge>
            <a
              href={`/portail/bilan/${player.id}`}
              data-testid="portail-bilan-link"
              className="text-xs font-bold uppercase tracking-wider bg-white text-emerald-700 px-4 py-2 rounded-md hover:bg-emerald-50 transition-colors"
            >
              📊 Mon bilan complet
            </a>
          </div>

          {/* Stats summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 border-t border-slate-100">
            <StatBox label="Matchs joués" value={totalMatches} />
            <StatBox label="Points totaux" value={totalPts} highlight />
            <StatBox label="Moy. points" value={avgPts} />
            <StatBox label="Rebonds" value={totalReb} />
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-slate-200">
          {[
            { id: "profil", label: "Mon profil", icon: User },
            { id: "calendrier", label: "Calendrier équipe", icon: CalendarDays },
            { id: "matchs", label: "Mes matchs", icon: Trophy },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id} onClick={() => setTab(t.id)}
                data-testid={`portail-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  tab === t.id
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {tab === "profil" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">État civil</div>
                <InfoRow label="Date de naissance" value={formatDate(player.date_of_birth)} />
                <InfoRow label="Nationalité" value={player.nationality} />
                <InfoRow label="Taille / Poids" value={`${player.height_cm || "—"} cm / ${player.weight_kg || "—"} kg`} />
                <InfoRow label="Club précédent" value={player.previous_club || "—"} />
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    Suivi médical
                  </div>
                  <Heart className="h-4 w-4 text-red-500" />
                </div>
                {(player.injuries || []).length === 0 ? (
                  <div className="text-sm text-slate-400 py-4 text-center">
                    Aucune blessure enregistrée. 🎉
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {player.injuries.slice(-3).reverse().map((inj) => (
                      <li key={inj.id} className="flex items-start gap-2 text-sm border-l-2 border-red-300 pl-3 py-1">
                        <div>
                          <div className="font-semibold">{inj.type}</div>
                          <div className="text-xs text-slate-500">
                            {formatDate(inj.date)} • {inj.recovery_status === "retabli" ? "Rétablie" : "En cours"}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {(player.progress_notes || []).length > 0 && (
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">
                    Observations récentes de l'entraîneur
                  </div>
                  <ul className="space-y-3">
                    {player.progress_notes.slice(-3).reverse().map((o) => (
                      <li key={o.id} className="border-l-2 border-emerald-300 pl-4 py-2">
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                          <span className="font-semibold">{o.author}</span>
                          <span>•</span>
                          <span>{formatDate(o.date)}</span>
                          {o.rating && (
                            <span className="flex gap-0.5 ml-1">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} className={`h-3 w-3 ${n <= o.rating ? "text-amber-500 fill-amber-500" : "text-slate-200"}`} />
                              ))}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{o.content}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {tab === "annonces" && (
            <div className="space-y-3">
              {announcements.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
                  <Megaphone className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                  <h3 className="font-barlow text-xl font-bold mb-1">Aucune annonce</h3>
                  <p className="text-sm text-slate-500">Vous serez notifiée des prochaines communications du club.</p>
                </div>
              ) : announcements.map((a) => {
                const pri = PRIORITY[a.priority] || PRIORITY.info;
                const PIcon = pri.icon;
                return (
                  <div key={a.id} data-testid={`portail-ann-${a.id}`}
                    className={`bg-white border-2 rounded-md p-5 ${a.read ? "border-slate-200" : "border-emerald-300 shadow-sm"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`h-9 w-9 rounded-md border flex items-center justify-center shrink-0 ${pri.color}`}>
                        <PIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {a.pinned && <Pin className="h-3.5 w-3.5 text-emerald-600" />}
                          <h3 className="font-barlow font-bold text-lg tracking-tight">{a.title}</h3>
                          {!a.read && (
                            <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {a.author_name} • {formatDate(a.created_at)}
                        </div>
                        <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{a.content}</p>
                        {!a.read && (
                          <Button size="sm" variant="outline" onClick={() => markRead(a.id)}
                            className="mt-3 text-xs"
                            data-testid={`mark-read-${a.id}`}>
                            Marquer comme lu
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "calendrier" && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">
                Prochains événements de l'équipe {player.category}
              </div>
              {upcoming.length === 0 ? (
                <div className="text-sm text-slate-400 py-8 text-center">
                  Aucun événement programmé.
                </div>
              ) : (
                <ul className="space-y-3">
                  {upcoming.map((e) => (
                    <li key={e.id} data-testid={`portail-event-${e.id}`}
                      className="flex items-center gap-4 border-l-2 border-emerald-300 pl-4 py-2">
                      <div className="text-center w-14 shrink-0">
                        <div className="font-barlow font-bold text-2xl text-emerald-700 leading-none">
                          {new Date(e.date).getDate()}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                          {new Date(e.date + "T12:00").toLocaleDateString("fr-FR", { month: "short" })}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold">{e.title}</div>
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">
                            {EVENT_LABELS[e.event_type]}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-3 text-xs text-slate-500 mt-1">
                          {e.start_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{e.start_time}</span>}
                          {e.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</span>}
                          {e.opponent && <span>vs <b>{e.opponent}</b></span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "matchs" && (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              {past.length === 0 ? (
                <div className="text-sm text-slate-400 py-12 text-center">
                  Aucun match joué pour le moment.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Match</th>
                      <th className="text-right p-3">Score</th>
                      <th className="text-center p-3">Min</th>
                      <th className="text-center p-3">Pts</th>
                      <th className="text-center p-3">Pas</th>
                      <th className="text-center p-3">Reb</th>
                    </tr>
                  </thead>
                  <tbody>
                    {past.map((m) => (
                      <tr key={m.id} data-testid={`portail-match-${m.id}`} className="border-b border-slate-100">
                        <td className="p-3 text-slate-600">{formatDate(m.event_date)}</td>
                        <td className="p-3 font-semibold">{m.event_title}</td>
                        <td className="p-3 text-right font-mono">
                          {m.result_home != null ? `${m.result_home} – ${m.result_away}` : "—"}
                        </td>
                        <td className="p-3 text-center">{m.minutes_played}</td>
                        <td className="p-3 text-center font-bold text-emerald-700">{m.points}</td>
                        <td className="p-3 text-center">{m.assists}</td>
                        <td className="p-3 text-center">{m.rebounds}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="mt-8 text-center text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5 inline mr-1 text-emerald-600" />
          Vous êtes connecté(e) en mode portail • Accès limité en lecture seule
        </div>
      </div>
      <HelpButton />
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className="p-5 text-center">
      <div className={`font-barlow font-bold text-3xl tracking-tight leading-none ${highlight ? "text-emerald-700" : "text-slate-900"}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">{label}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-900">{value || "—"}</span>
    </div>
  );
}